/**
 * 电台 —— 多听
 * ============================================================
 * 学方言要「多听多说」，这是「多听」的入口：接几个方言电台，
 * 用户可以随时打开当背景音磨耳朵。
 *
 * ── 技术上守住的三条 ────────────────────────────────────────
 *   1. **流地址在数据文件里，不在代码里**（lib/radioStations.js）。
 *      电台流极易失效，换地址只改数据文件，不用动组件。
 *   2. **HLS 用 hls.js，原生 mp3/aac 直链用 <audio> 直连**。
 *      Android WebView / Chrome 不认 m3u8，必须 hls.js；
 *      Safari 原生支持，走 else 分支。
 *   3. **流挂了要如实说**。断流或加载失败时显示「暂不可用」，
 *      而不是永远转圈 —— 用户分不清是"网络慢"还是"台没了"。
 *
 * 无第三方授权：RTHK 是公共广播官方公开流；内地台若接入的是
 * 聚合站流，有版权/稳定性风险，故暂只放「已验证能播」的台。
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import { STATIONS } from '../lib/radioStations';
import BottomNav from '../components/BottomNav';

export default function RadioPage() {
  const audioRef = useRef(null);
  const hlsRef = useRef(null);

  const [currentId, setCurrentId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const current = STATIONS.find((s) => s.id === currentId) || null;

  /** 停掉当前播放（含 hls 实例） */
  const teardown = useCallback(() => {
    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {
        /* 忽略 */
      }
      hlsRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
  }, []);

  /** 播放某个台 */
  const play = useCallback(
    (station) => {
      if (!station || !station.streamUrl || station.status === 'offline') return;

      // 点同一个台 = 切换播放/暂停
      if (currentId === station.id) {
        const a = audioRef.current;
        if (!a) return;
        if (playing) {
          a.pause();
          setPlaying(false);
        } else {
          a.play().catch(() => setError('播放被浏览器拦截，请重试'));
          setPlaying(true);
        }
        return;
      }

      teardown();
      setCurrentId(station.id);
      setError(null);
      setLoading(true);
      setPlaying(true);
    },
    [currentId, playing, teardown]
  );

  /** currentId 变化后，真正加载流 */
  useEffect(() => {
    if (!current || !current.streamUrl) return;

    const audio = audioRef.current;
    if (!audio) return;

    const url = current.streamUrl;
    const isM3u8 = /\.m3u8($|\?)/i.test(url);

    if (isM3u8 && Hls.isSupported()) {
      // Android / Chrome：用 hls.js
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(audio);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false);
        audio.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          setError('这个台暂时连不上，可能正在维护');
          setLoading(false);
          setPlaying(false);
        }
      });
    } else if (isM3u8 && audio.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari：原生 HLS
      audio.src = url;
      audio.addEventListener(
        'loadedmetadata',
        () => {
          setLoading(false);
          audio.play().catch(() => {});
        },
        { once: true }
      );
    } else {
      // 直链 mp3/aac
      audio.src = url;
      const onCanPlay = () => {
        setLoading(false);
        audio.play().catch(() => {});
      };
      audio.addEventListener('canplay', onCanPlay, { once: true });
    }

    audio.onerror = () => {
      setError('这个台暂时连不上');
      setLoading(false);
      setPlaying(false);
    };

    return () => {
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f172a] via-[#1e293b] to-[#0f172a] pb-28">
      {/* 顶部标题 */}
      <header className="px-6 pt-12 pb-4 text-center">
        <h1 className="text-2xl font-bold text-white">方言电台</h1>
        <p className="mt-1 text-sm text-slate-400">多听 · 让耳朵先熟悉这种话</p>
      </header>

      {/* 收音机主体 */}
      <main className="mx-auto max-w-md px-5">
        {/* 收音机造型 */}
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-teal-500 to-sky-600 p-1 shadow-2xl shadow-teal-900/40">
          <div className="rounded-[1.8rem] bg-[#0b1220] p-6">
            {/* 频率显示屏 */}
            <div className="flex items-center justify-between rounded-xl bg-black/50 px-4 py-3 font-mono">
              <div className="text-teal-300">
                {current ? (
                  <>
                    <div className="text-lg font-bold leading-tight text-teal-200">
                      {current.name}
                    </div>
                    <div className="text-[11px] text-teal-400/70">{current.freq}</div>
                  </>
                ) : (
                  <div className="text-lg text-slate-500">选择电台</div>
                )}
              </div>

              {/* 播放状态灯 */}
              <div className="flex flex-col items-center gap-1">
                <span
                  className={`inline-block h-3 w-3 rounded-full ${
                    playing ? 'animate-pulse bg-rose-500' : 'bg-slate-600'
                  }`}
                />
                <span className="text-[10px] text-slate-500">
                  {loading ? '连接中' : playing ? '直播中' : '待机'}
                </span>
              </div>
            </div>

            {/* 方言标签 */}
            {current && (
              <div className="mt-4 text-center">
                <span className="rounded-full bg-teal-500/20 px-3 py-1 text-xs font-bold text-teal-300">
                  {current.dialect}
                </span>
              </div>
            )}

            {/* 说明文字 */}
            <p className="mt-4 min-h-[2.5rem] text-center text-[12px] leading-relaxed text-slate-400">
              {current ? current.desc : '从下面的电台里挑一个，开始磨耳朵。'}
            </p>

            {error && (
              <p className="mt-2 text-center text-[11px] text-amber-400">⚠️ {error}</p>
            )}
          </div>
        </div>

        {/* 电台列表 */}
        <div className="mt-5 space-y-3">
          {STATIONS.map((s) => {
            const active = s.id === currentId;
            const unavailable = s.status === 'offline' || !s.streamUrl;
            return (
              <button
                key={s.id}
                type="button"
                disabled={unavailable}
                onClick={() => play(s)}
                className={`flex w-full items-center gap-3 rounded-2xl p-4 text-left transition ${
                  active
                    ? 'bg-teal-500/15 ring-1 ring-teal-400'
                    : unavailable
                      ? 'bg-white/5 opacity-45'
                      : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                {/* 台标圆点 */}
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg ${
                    active ? 'bg-teal-500 text-white' : 'bg-white/10 text-slate-300'
                  }`}
                >
                  {active && playing ? '▶' : '◉'}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-white">
                    {s.name}
                  </span>
                  <span className="block text-[11px] text-slate-400">
                    {s.freq} · {s.dialect}
                  </span>
                </span>

                {unavailable ? (
                  <span className="shrink-0 rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-slate-500">
                    暂不可用
                  </span>
                ) : active && playing ? (
                  <span className="shrink-0 text-teal-300">正在播放</span>
                ) : null}
              </button>
            );
          })}
        </div>

        {/* 说明 */}
        <p className="mt-6 text-center text-[10px] leading-relaxed text-slate-500">
          电台流为各广播机构公开提供，可能随时调整。
          <br />
          断流时请稍后再试；「暂不可用」的台待接入稳定源后开放。
        </p>
      </main>

      <audio ref={audioRef} className="hidden" />
      <BottomNav />
    </div>
  );
}
